import React, { forwardRef, useState, useEffect, useMemo, useId } from 'react';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var _excluded = ["className", "content", "title", "role", "aria-labelledby", "aria-hidden"];

// Custom hook to grab a default iconId using React.useId()
// NOTE: useId is only available in React 18+
var useDefaultIconId = () => {
  var reactId = useId === null || useId === void 0 ? void 0 : useId();
  if (reactId) return "icon_".concat(reactId);
  return undefined;
};
var Icon = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
      className,
      content,
      title,
      role,
      'aria-labelledby': ariaLabelledby,
      'aria-hidden': ariaHidden
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var [iconId, setIconId] = useState(useDefaultIconId());
  useEffect(() => {
    // If using React < 18, use UUID to render the unique iconId
    // UUID v4 causes hydration issues in SSR because it consumes crypto.
    //    Crypto is a built in node.js package and is not consumed properly in Next.js
    //    So UUID value needs to be set from useEffect which renders client side.
    if (!iconId) {
      setIconId("icon_".concat(v4()));
    }
  }, [iconId]);
  var {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby
  } = otherProps;
  var hasLabel = !!title || !!ariaLabelledby || !!ariaLabel || !!ariaDescribedby;
  return useMemo(() => {
    var _content$props;
    if (!content) return null;
    return /*#__PURE__*/React.cloneElement(content, _objectSpread2({
      children: /*#__PURE__*/React.createElement(React.Fragment, null, title ? /*#__PURE__*/React.createElement("title", {
        id: iconId
      }, title) : null, content === null || content === void 0 ? void 0 : (_content$props = content.props) === null || _content$props === void 0 ? void 0 : _content$props.children),
      className,
      ref,
      focusable: false,
      role,
      'aria-labelledby': ariaLabelledby || (title ? iconId : undefined),
      'aria-hidden': typeof ariaHidden !== 'undefined' ? ariaHidden : !hasLabel
    }, otherProps));
  }, [content, className, title, role, hasLabel, ref, ariaLabelledby, ariaHidden, otherProps, iconId]);
});
var propTypes = {
  /** Node for the individual icon import */
  content: PropTypes.node.isRequired,
  /** Optional class to be applied to the SVG element */
  className: PropTypes.string,
  /** Value for the <title> element describing the icon */
  title: PropTypes.string,
  role: PropTypes.string,
  /** By default points to the <title> element, if provided. */
  'aria-labelledby': PropTypes.string,
  /** By default is set to 'true' if no title or label is provided. */
  'aria-hidden': PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};
var defaultProps = {
  className: null,
  title: '',
  role: 'img',
  'aria-labelledby': undefined,
  'aria-hidden': undefined
};
Icon.propTypes = propTypes;
Icon.defaultProps = defaultProps;

export { Icon, defaultProps, propTypes };
//# sourceMappingURL=index.js.map
